/**
 * VireoHR - Manager Schedule Screen
 * Ultra-Minimal Design - Manager can only manage assigned locations
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useScheduleData } from '@/hooks/useScheduleData';
import { createSchedule } from '@/services/cloudFunctions';


export default function ManagerSchedule() {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Use shared hook for schedule data (manager-filtered)
  const { schedules, branches, employees, loading, refetch } = useScheduleData({
    tenantId: user?.tenantId || '',
    role: 'manager',
    allowedLocationIds: user?.managerLocationIds || [],
    filterFutureOnly: true
  });
  
  const [formData, setFormData] = useState({
    branchId: '',
    employeeIds: [] as string[],
    date: '',
    startTime: '',
    endTime: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.tenantId || !formData.branchId || formData.employeeIds.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    // Verify manager can manage this location
    if (!user.managerLocationIds?.includes(formData.branchId)) {
      toast.error('You can only create schedules for your assigned locations');
      return;
    }

    try {
      const loadingToast = toast.loading('Creating schedule...');
      
      const startDateTime = new Date(`${formData.date}T${formData.startTime}:00`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}:00`);

      const result = await createSchedule({
        tenantId: user.tenantId,
        branchId: formData.branchId,
        employeeIds: formData.employeeIds,
        startTimestamp: startDateTime.toISOString(),
        endTimestamp: endDateTime.toISOString(),
      });

      toast.dismiss(loadingToast);

      if (result.data.success) {
        toast.success('Schedule created successfully');
        setShowCreateForm(false);
        resetForm();
        refetch();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      branchId: '',
      employeeIds: [],
      date: '',
      startTime: '',
      endTime: '',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout title="Schedules">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Schedules">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-gray-900">Schedules</h1>
          <Button onClick={() => setShowCreateForm(true)} variant="primary">
            Create
          </Button>
        </div>

        {/* Info */}
        <Card>
          <p className="text-xs text-gray-500">
            You can manage schedules for {branches.length} location{branches.length !== 1 ? 's' : ''}
          </p>
        </Card>

        {schedules.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No upcoming schedules</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <Card key={schedule.id}>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {formatDateTime(schedule.startTimestamp)}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {schedule.branchName}
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Employees</p>
                    <div className="flex flex-wrap gap-1">
                      {schedule.employeeNames?.map((name, idx) => (
                        <Badge key={idx} variant="default">{name}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Form - Similar to Owner but filtered locations */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Create Schedule</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                >
                  <option value="">Select location</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1" variant="primary">Create</Button>
                <Button type="button" onClick={() => { setShowCreateForm(false); resetForm(); }} variant="outline" className="flex-1">Cancel</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </Layout>
  );
}
